import { ReactNode } from 'react'
import { VMHAStatusDetails } from 'api-client/model'

export enum RebalancingStrategy {
  VMWorkloadConsolidation = 'vm_workload_consolidation',
  NodeResourceConsolidation = 'node_resource_consolidation',
}

export enum Status {
  Healthy = 'Active',
  Unhealthy = 'Inactive',
  Maintenance = 'Maintenance',
  Error = 'Error',
  Unknown = 'Unknown',
}

export enum RebalancingFrequency {
  Default = 20,
  Aggressive = 10,
  Conservative = 30,
}

export interface AutoResourceRebalancing {
  enabled: boolean
  rebalancingStrategy: RebalancingStrategy
  rebalancingFrequencyMins: number
}

export enum EvacuationSpeed {
  Conservative = 'conservative',
  Balanced = 'balanced',
  Aggressive = 'aggressive',
}

export interface VmhaConfig {
  mode?: EvacuationSpeed
  host_config?: { nfs_heartbeat_path: string }
  exclude_ephemeral_vms?: boolean
}

export interface VmhaConfigBody {
  mode: EvacuationSpeed
  host_config?: { nfs_heartbeat_path: string }
  exclude_ephemeral_vms?: boolean
}

// PCD-4129: VM-HA capacity reserve — the percentage of a cluster's
// allocated CPU/RAM that must stay free for failover, enforced by
// pf9-nova's ClusterAllocationThresholdFilter. capacity_reserve_enabled is
// a separate opt-in from VM-HA itself: a cluster can have VM-HA on but the
// reserve turned off.
export interface AllocationThresholdConfig {
  allocation_threshold_percent?: number
  capacity_reserve_enabled?: boolean
}

export interface AllocationThresholdBody {
  allocation_threshold_percent: number
  capacity_reserve_enabled: boolean
}

export interface VMHighAvailability {
  enabled: boolean
}

export interface Gpu {
  enabled: boolean
  mode: string
}

export interface Cpu {
  mode: string
  model: string
}

export interface CreateClusterHostGroup {
  name: string
  vmHighAvailability: VMHighAvailability
  autoResourceRebalancing: AutoResourceRebalancing
  gpu: Gpu
  cpu: Cpu
}

export enum VMHADisplayStatus {
  PROTECTED = 'Protected',
  NOT_PROTECTED = 'Not Protected',
  DEGRADED = 'Degraded',
  DISABLED = 'Disabled',
  EXCLUDED = 'Excluded',
  UNKNOWN = 'Unknown',
  LOADING = 'Loading',
}

export interface VMHACondition {
  key: string
  label: string | ReactNode
  passed?: boolean
  degraded?: boolean
}

export type EditClusterHostGroup = CreateClusterHostGroup

export interface ClusterHostGroup {
  id?: string
  name: string
  vmHighAvailability: VMHighAvailability
  autoResourceRebalancing: AutoResourceRebalancing
  hostlist: string[]
  status: Status
  gpu?: Gpu
  cpu?: Cpu
}

export interface IClusterHostGroup extends ClusterHostGroup {
  vmhaStatus?: VMHADisplayStatus
  vmhaState?: VMHAStatusDetails
  hosts?: Array<any>
  physicalNetworkLabels: string[]
  totalUsage: {
    cpuUsage: {
      current: number
      total: number
    }
    memoryUsage: {
      current: number
      total: number
    }
    storageUsage: {
      current: number
      total: number
    }
  }
}
